// Tour Utilities for Analytics Tracking
// Helper functions for tour identification, step categorization, and analytics

// ============================================================================
// TOUR NAME MAPPING
// ============================================================================

export const getTourDisplayName = (tourName: string): string => {
  const tourNames: Record<string, string> = {
    settings: 'User Settings Tour',
    notifications: 'Notifications Tour',
    workbench: 'Workbench Tour',
    user_settings: 'User Settings Tour',
    notifications_tour: 'Notifications Tour',
    workbench_tour: 'Workbench Tour',
    // Add more tours as needed
  };
  return (
    tourNames[tourName] ||
    `${
      tourName.charAt(0).toUpperCase() + tourName.slice(1).replace(/_/g, ' ')
    } Tour`
  );
};

// ============================================================================
// STEP IDENTIFICATION
// ============================================================================

export const getStepName = (step: {
  selector?: string;
  content?: {
    props?: {
      title?: string;
      children?: {
        props?: {
          title?: string;
        };
      };
    };
  };
}): string => {
  // Extract step name from content or selector
  if (step.content?.props?.title) {
    return step.content.props.title;
  }
  if (step.content?.props?.children?.props?.title) {
    return step.content.props.children.props.title;
  }
  if (step.selector) {
    return step.selector
      .replace('[data-tour="', '')
      .replace('"]', '')
      .replace(/-/g, ' ');
  }
  return `Step ${step.selector || 'unknown'}`;
};

export const getStepCategory = (step: {
  selector?: string;
  content?: {
    props?: {
      title?: string;
      children?: {
        props?: {
          title?: string;
        };
      };
    };
  };
}): string => {
  // Categorize steps for analytics
  const selector = step.selector || '';
  const stepName = getStepName(step).toLowerCase();

  if (selector.includes('header') || stepName.includes('header')) {
    return 'header';
  }
  if (selector.includes('card') || stepName.includes('card')) {
    return 'card';
  }
  if (selector.includes('button') || stepName.includes('button')) {
    return 'button';
  }
  if (selector.includes('filter') || stepName.includes('filter')) {
    return 'filter';
  }
  if (selector.includes('tour') || stepName.includes('tour')) {
    return 'tour_intro';
  }
  if (selector.includes('help') || stepName.includes('help')) {
    return 'help';
  }
  if (selector.includes('notification') || stepName.includes('notification')) {
    return 'notification';
  }
  if (selector.includes('setting') || stepName.includes('setting')) {
    return 'setting';
  }

  return 'general';
};

// ============================================================================
// TOUR DURATION TRACKING
// ============================================================================

export const calculateTourDuration = (startTime: number): number => {
  return Date.now() - startTime;
};

export const calculateStepDuration = (stepStartTime: number): number => {
  return Date.now() - stepStartTime;
};

// ============================================================================
// TOUR CONTEXT HELPERS
// ============================================================================

export const getPageContext = (tourName: string): string => {
  const contextMap: Record<string, string> = {
    settings: 'user_settings',
    user_settings: 'user_settings',
    user_settings_tour: 'user_settings',
    notifications: 'notification',
    notifications_tour: 'notification',
    workbench: 'workbench',
    workbench_tour: 'workbench',
  };
  return contextMap[tourName] || 'unknown';
};

// ============================================================================
// DEBUG HELPERS
// ============================================================================

export const logTourEvent = (
  _eventType: string,
  _data: Record<string, unknown>
): void => {
  // Debug logging removed
};

export const logDatadogEvent = (
  _eventType: string,
  _data: Record<string, unknown>
): void => {
  // Debug logging removed
};
